import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Briefcase, TrendingUp } from 'lucide-react';

const KanbanView = ({ investments, stages, onStageChange, currency }) => {
  const navigate = useNavigate();
  const [draggedItem, setDraggedItem] = React.useState(null);

  // Group investments by stage
  const investmentsByStage = stages.reduce((acc, stage) => {
    acc[stage] = investments.filter(inv => (inv.investment_stage || 'Pipeline') === stage);
    return acc;
  }, {});

  // Calculate totals per stage
  const getStageTotals = (stageInvestments) => {
    const count = stageInvestments.length;
    // You can add total investment value calculation here if needed
    return { count };
  };

  const handleDragStart = (e, investment) => {
    setDraggedItem(investment);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    if (draggedItem && draggedItem.investment_stage !== targetStage) {
      onStageChange(draggedItem.id, targetStage);
    }
    setDraggedItem(null);
  };

  const getStageColor = (stage) => {
    const colors = {
      'Pipeline': 'border-gray-300 bg-gray-50',
      'Active': 'border-green-300 bg-green-50',
      'Under Review': 'border-yellow-300 bg-yellow-50',
      'Hold': 'border-orange-300 bg-orange-50',
      'Watchlist': 'border-purple-300 bg-purple-50',
      'Exited': 'border-red-300 bg-red-50'
    };
    return colors[stage] || 'border-gray-300 bg-gray-50';
  };

  const getStageHeaderColor = (stage) => {
    const colors = {
      'Pipeline': 'bg-gray-600',
      'Active': 'bg-green-600',
      'Under Review': 'bg-yellow-600',
      'Hold': 'bg-orange-600',
      'Watchlist': 'bg-purple-600',
      'Exited': 'bg-red-600'
    };
    return colors[stage] || 'bg-gray-600';
  };

  if (investments.length === 0) {
    return (
      <div className="text-center py-12">
        <TrendingUp size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600">No investments found</p>
      </div>
    );
  }

  return (
    <div className="flex space-x-4 overflow-x-auto pb-4">
      {stages.map((stage) => {
        const stageInvestments = investmentsByStage[stage] || [];
        const totals = getStageTotals(stageInvestments);

        return (
          <div
            key={stage}
            className="flex-shrink-0 w-80"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage)}
          >
            {/* Stage Header */}
            <div className={`${getStageHeaderColor(stage)} text-white rounded-t-lg p-4`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-lg">{stage}</h3>
                <span className="bg-white bg-opacity-25 px-2 py-1 rounded-full text-sm font-medium">
                  {totals.count}
                </span>
              </div>
              <div className="flex items-center text-sm opacity-90">
                <Briefcase size={14} className="mr-1" />
                <span>{totals.count} {totals.count === 1 ? 'Investment' : 'Investments'}</span>
              </div>
            </div>

            {/* Stage Column */}
            <div className={`min-h-[500px] ${getStageColor(stage)} border-2 border-t-0 rounded-b-lg p-3 space-y-3`}>
              {stageInvestments.map((investment) => (
                <div
                  key={investment.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, investment)}
                  onClick={() => navigate(`/investments/${investment.id}`)}
                  className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 p-4 cursor-move border border-gray-200"
                >
                  <h4 className="font-semibold text-gray-900 mb-2 truncate">
                    {investment.portfolio_company_name}
                  </h4>
                  
                  {investment.sector && (
                    <p className="text-xs text-gray-600 mb-2">
                      <span className="font-medium">Sector:</span> {investment.sector}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 mt-3 pt-3 border-t">
                    <div className="flex items-center">
                      <DollarSign size={12} className="mr-1" />
                      {investment.investment_currency}
                    </div>
                    {investment.types_of_securities_offered?.length > 0 && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded">
                        {investment.types_of_securities_offered[0]}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              
              {stageInvestments.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  Drop investments here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KanbanView;
